import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const STEPS = [
  {
    title: 'Welcome to Tajweed Tutor',
    description: 'Improve your Qur\'anic recitation with AI-powered feedback on tajweed rules.',
    icon: (
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-3xl font-bold font-arabic">
        ت
      </div>
    ),
  },
  {
    title: 'Choose Your Level',
    description: 'We\'ll tailor feedback to your experience. You can change this anytime.',
    icon: (
      <svg className="w-16 h-16 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
      </svg>
    ),
    hasLevelSelect: true,
  },
  {
    title: 'Record & Get Feedback',
    description: 'Select an ayah, listen to a reference reciter, then record yourself. Our AI will highlight errors and suggest drills.',
    icon: (
      <svg className="w-16 h-16 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
      </svg>
    ),
  },
]

const LEVELS = [
  { id: 'beginner', label: 'Beginner', desc: 'Learning the basics of tajweed' },
  { id: 'intermediate', label: 'Intermediate', desc: 'Familiar with rules, refining pronunciation' },
  { id: 'advanced', label: 'Advanced', desc: 'Fine-tuning for mastery' },
]

interface OnboardingProps {
  onComplete: (level: string) => void
}

const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const [step, setStep] = useState(0)
  const [level, setLevel] = useState('intermediate')

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1)
    } else {
      localStorage.setItem('tajweed_onboarded', 'true')
      localStorage.setItem('tajweed_level', level)
      onComplete(level)
    }
  }

  const currentStep = STEPS[step]

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 max-w-md w-full p-8 shadow-2xl"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="text-center"
          >
            <div className="flex justify-center mb-6">{currentStep.icon}</div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{currentStep.title}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{currentStep.description}</p>

            {currentStep.hasLevelSelect && (
              <div className="space-y-2 mb-6">
                {LEVELS.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setLevel(l.id)}
                    className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                      level === l.id
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <span className={`text-sm font-medium ${level === l.id ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-white'}`}>
                      {l.label}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{l.desc}</p>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Progress dots + button */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === step ? 'bg-emerald-500' : i < step ? 'bg-emerald-300 dark:bg-emerald-700' : 'bg-gray-300 dark:bg-gray-700'
                }`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {step === 0 && (
              <button
                onClick={() => {
                  localStorage.setItem('tajweed_onboarded', 'true')
                  onComplete('intermediate')
                }}
                className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                Skip
              </button>
            )}
            <button onClick={handleNext} className="btn-primary text-sm py-2 px-6">
              {step === STEPS.length - 1 ? 'Start Practicing' : 'Next'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default Onboarding
